import { Link } from "react-router-dom";
import { Target, Compass, ShieldCheck, Leaf, Cog, Handshake, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const VALUES = [
  { icon: Leaf, title: "Planet Positive", desc: "Every plant we engineer must leave the environment measurably better than it found it." },
  { icon: Cog, title: "Engineering Precision", desc: "Heavy-industry rigour applied to green technology — no compromises on performance." },
  { icon: Handshake, title: "Turnkey Integrity", desc: "One accountable partner from concept through commissioning and operation." },
];

const TIMELINE = [
  { phase: "Focus", text: "Green decarbonisation and clean energy technologies for heavy industry." },
  { phase: "Breadth", text: "Metals & steel recycling, biomass energy, waste valorization, renewables generation & storage, water & air treatment." },
  { phase: "Delivery", text: "Air separation, pollution control and waste-to-energy folded into complete turnkey plant offerings." },
  { phase: "Net Zero", text: "One circular-economy mission: every waste stream engineered into a new value chain." },
];

const About = () => (
  <div data-testid="about-page" className="pt-28 sm:pt-36">
    <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow>About us</Eyebrow>
        <h1 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
          Engineering the planet's <span className="text-gradient">next industrial revolution</span>
        </h1>
        <p className="mt-6 max-w-2xl text-gray-300 text-base sm:text-lg leading-relaxed">
          Green Planet Technologies Pty Ltd is a circular economy, clean energy, planet-friendly
          organization with Net Zero ambition — turning industrial waste streams into energy,
          materials, fuel and restored land.
        </p>
      </Reveal>
    </header>

    {/* VISION / MISSION */}
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 grid md:grid-cols-2 gap-6">
      <Reveal>
        <div data-testid="vision-card" className="h-full rounded-2xl border border-lime/30 bg-gradient-to-br from-lime/10 to-emerald/5 p-8">
          <Target className="text-lime" size={28} />
          <h2 className="mt-6 font-display font-bold text-2xl text-white">Vision</h2>
          <p className="mt-3 text-gray-300 leading-relaxed">
            To be a global leader of the circular economy — where every waste stream feeds a new
            value chain and heavy industry runs in balance with the planet.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div data-testid="mission-card" className="h-full rounded-2xl border border-white/10 bg-steel p-8">
          <Compass className="text-lime" size={28} />
          <h2 className="mt-6 font-display font-bold text-2xl text-white">Mission</h2>
          <p className="mt-3 text-gray-300 leading-relaxed">
            To deliver concept-to-commissioning sustainable engineering: steel and metals recycling,
            renewable generation & storage, waste-to-energy, air & water treatment and land
            restoration — installed, commissioned and operated by one accountable partner.
          </p>
        </div>
      </Reveal>
    </section>

    {/* VALUES */}
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
      <Reveal>
        <Eyebrow>Core values</Eyebrow>
        <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl">What we stand for</h2>
      </Reveal>
      <div className="mt-10 grid sm:grid-cols-3 gap-6">
        {VALUES.map((v, i) => (
          <Reveal key={v.title} delay={i * 0.08}>
            <div data-testid={`value-card-${i + 1}`} className="h-full rounded-2xl border border-white/10 bg-carbon p-8 hover:border-lime/40 hover:-translate-y-1 transition-all duration-300">
              <v.icon className="text-lime" size={26} />
              <h3 className="mt-5 font-display font-bold text-xl text-white">{v.title}</h3>
              <p className="mt-3 text-sm text-gray-400 leading-relaxed">{v.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* APPROACH */}
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 pb-24">
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <div className="relative rounded-2xl border border-white/10 lg:sticky lg:top-28 bg-gradient-to-br from-lime/10 via-carbon to-obsidian p-10 min-h-[320px] sm:min-h-[420px] flex flex-col justify-between overflow-hidden">
            <Leaf className="absolute -bottom-10 -right-10 text-lime/10" size={230} />
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-lime">Green Planet Technologies</p>
            <p className="relative font-display font-extrabold text-5xl sm:text-6xl leading-none">
              <span className="text-white">NET</span> <span className="text-gradient">ZERO</span>
            </p>
          </div>
        </Reveal>
        <div>
          <Reveal>
            <Eyebrow>Our approach</Eyebrow>
            <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl">From ore to Net Zero</h2>
          </Reveal>
          <div className="mt-10 space-y-0">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.phase} delay={i * 0.08}>
                <div data-testid={`timeline-item-${i + 1}`} className="relative pl-10 pb-10 border-l border-white/10 last:pb-0">
                  <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-lime shadow-[0_0_12px_rgba(132,204,22,0.7)]" />
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-lime">{t.phase}</p>
                  <p className="mt-2 text-gray-300 leading-relaxed">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-8 flex items-start gap-3 rounded-2xl border border-white/10 bg-steel p-6">
              <ShieldCheck className="text-lime shrink-0 mt-0.5" size={22} />
              <p className="text-sm text-gray-300 leading-relaxed">
                Green Planet Technologies offers consultancy from concept to creation —
                installation, commissioning and operation included.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="about-cta-link"
              className="mt-8 inline-flex items-center gap-2 bg-lime text-obsidian font-semibold px-6 py-3 rounded-full hover:bg-lime-bright transition-all duration-300"
            >
              Work with us <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  </div>
);

export default About;
